/**
 * Is a media's video ready for the player to fetch and decode? (DEV-530)
 *
 * `playbackType: 'VIDEO_NP'` is a placeholder meaning "versions aren't
 * generated yet — skip fetch+decode", and the player renders such a scene
 * **black**. The only thing that clears it is
 * `EditorStateService.clearPlaybackTypeForMedia()`, fired from the
 * version-generation job-completion event.
 *
 * That makes the flag safe to write exactly once: on a media whose generation
 * job is about to run. Write it on a media that is already generated and no job
 * runs, no completion event fires, and nothing ever clears it — the scene stays
 * black forever. That is the re-added-scene bug: delete a scene from the
 * timeline, leave the media in the pool (its versions are not pruned), drop it
 * back, and the `versionCount <= 1` generation gate skips the work *and* the
 * bookkeeping.
 *
 * So the stamp and the generation trigger must read the same predicate. This is
 * that predicate, previously inlined in `timeline-panel.ts` as
 * `mediaHasVersions`.
 */

export interface MediaReadiness {
	isLocal?: boolean;
	versionCount?: number;
}

/**
 * External media (imported from a CDN) always arrives with every variant
 * already generated — the platform never generates for it, so it is ready by
 * definition. Local media is ready once generation has produced something
 * beyond the `original`.
 */
export function mediaHasGeneratedVersions(media: MediaReadiness): boolean {
	if (media.isLocal === false) return true;
	return (media.versionCount ?? 0) > 1;
}

/**
 * Whether a scene built on this media should carry the `VIDEO_NP` placeholder.
 *
 * An **unresolved** media (not in the project's pool) counts as ready and is
 * not stamped. The asymmetry is deliberate: a wrongly-stamped scene is black
 * permanently and silently, while a wrongly-unstamped one merely fetches a
 * version that is still being written — a transient, visible error during a job
 * that is running anyway.
 */
export function shouldStampVideoNp(media: MediaReadiness | undefined | null): boolean {
	if (!media) return false;
	return !mediaHasGeneratedVersions(media);
}
